import api from './api';
import {
  DecideOrganizerAccessRequest,
  Organizer,
  GrantOrganizerAccessRequest,
  OrganizerMember,
  OrganizerAssetProposal,
  PageResponse,
  PublicOrganizer,
  RegisterOrganizerRequest,
  RegisterOrganizerResponse,
  RequestOrganizerAccessRequest,
  UserLookupResult,
} from '@radevents/shared';

/**
 * Verknuepfung zwischen Konto und Veranstalter.
 *
 * Eigenes Pfad-Praefix `/organizer-access`, nicht `/organizers/...`: dort greift im Backend ein
 * Auffang-Matcher fuer Redaktionsrechte, unter dem ein Nutzer-Endpunkt unerreichbar waere.
 */
const organizerAccessService = {
  /** "Ich bin der Veranstalter" von der oeffentlichen Veranstalter-Seite. */
  /**
   * Den Einmal-Link aus der Outreach-Mail einlösen. 404 heißt: verbraucht, abgelaufen oder
   * erfunden — die drei Fälle sind für den Aufrufer derselbe.
   */
  claim: async (token: string): Promise<OrganizerMember> => {
    const response = await api.post('/organizer-access/claim', { token });
    return response.data;
  },

  request: async (data: RequestOrganizerAccessRequest): Promise<OrganizerMember> => {
    const response = await api.post('/organizer-access/request', data);
    return response.data;
  },

  /**
   * "Veranstalter eintragen" — fuer Vereine und Anbieter, die im Verzeichnis fehlen. Geht auch
   * ohne Konto (dann ein Vorschlag), deshalb unter /organizer-edits und nicht hier im Praefix.
   */
  register: async (data: RegisterOrganizerRequest): Promise<RegisterOrganizerResponse> => {
    const response = await api.post('/organizer-edits/register', data);
    return response.data;
  },

  /**
   * Steht der Veranstalter vielleicht schon im Verzeichnis, nur anders geschrieben?
   *
   * Gefragt wird waehrend des Tippens im Dialog, nicht erst beim Absenden: `register` faengt nur
   * den **genau** gleichen Namen und die gleiche Domain ab, ein Tippfehler rutscht durch und
   * muss spaeter von Hand zusammengefuehrt werden.
   */
  lookup: async (params: { name?: string; website?: string; email?: string }): Promise<PublicOrganizer[]> => {
    const response = await api.get('/organizers/lookup', { params });
    return response.data;
  },

  /**
   * Den Freischalt-Link einloesen. Die Antwort nennt Veranstalter und Antragsteller, damit die
   * Seite mit einem Aufruf auskommt: geklickt wurde schon in der Mail.
   */
  approveByLink: async (token: string): Promise<{ organizerName: string; requesterName: string; state: string }> => {
    const response = await api.post('/organizer-access/approval-link', { token });
    return response.data;
  },

  /** Eigene Verknuepfungen samt Antragsstand. */
  mine: async (): Promise<OrganizerMember[]> => {
    const response = await api.get('/organizer-access/mine');
    return response.data;
  },

  pending: async (page = 0, size = 20): Promise<PageResponse<OrganizerMember>> => {
    const response = await api.get('/organizer-access/pending', { params: { page, size } });
    return response.data;
  },

  pendingCount: async (): Promise<{ count: number }> => {
    const response = await api.get('/organizer-access/pending-count');
    return response.data;
  },

  decide: async (id: number, data: DecideOrganizerAccessRequest): Promise<OrganizerMember> => {
    const response = await api.post(`/organizer-access/${id}/decide`, data);
    return response.data;
  },

  grant: async (data: GrantOrganizerAccessRequest): Promise<OrganizerMember> => {
    const response = await api.post('/organizer-access/grant', data);
    return response.data;
  },

  revoke: async (id: number): Promise<void> => {
    await api.delete(`/organizer-access/${id}`);
  },

  assetProposals: async (): Promise<OrganizerAssetProposal[]> => {
    const response = await api.get('/organizer-access/asset-proposals');
    return response.data;
  },

  reviewAsset: async (id: number, approved: boolean, note?: string): Promise<void> => {
    await api.post(`/organizer-access/asset-proposals/${id}/review`, { approved, note });
  },

  byOrganizer: async (organizerId: number): Promise<OrganizerMember[]> => {
    const response = await api.get(`/organizer-access/by-organizer/${organizerId}`);
    return response.data;
  },

  /**
   * Schaltet die automatische Freigabe für einen Veranstalter.
   *
   * Vertrauensentscheidung der Redaktion: seine Änderungen gehen dann sofort live, statt in der
   * Prüfliste zu warten. Protokolliert werden sie weiterhin.
   */
  setAutoApprove: async (organizerId: number, enabled: boolean): Promise<Organizer> => {
    const response = await api.post(`/organizer-access/organizers/${organizerId}/auto-approve`, { enabled });
    return response.data;
  },

  /** Schmale Nutzersuche: ab drei Zeichen, hoechstens zehn Treffer (Grenzen setzt das Backend). */
  lookupUsers: async (q: string): Promise<UserLookupResult[]> => {
    const response = await api.get('/organizer-access/user-lookup', { params: { q } });
    return response.data;
  },
};

export default organizerAccessService;
